import React, { useState, useMemo } from "react";
import {
  SPECIALIZATION_DIRECTIONS,
  SPECIALIZATIONS,
  findSpecialization,
  findDirectionForSpecialization,
  type SpecializationItem,
} from "../constants/specializations.js";
import { Search, ArrowRight, ArrowLeft, Check } from "lucide-react";

export interface SpecializationPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  disabledOptions?: string[];
}

export function SpecializationPicker({
  value,
  onChange,
  disabled,
  disabledOptions = [],
}: SpecializationPickerProps): React.ReactElement {
  const [selectedDirectionId, setSelectedDirectionId] = useState<string | null>(() => {
    if (value) {
      const dir = findDirectionForSpecialization(value);
      return dir ? dir.id : null;
    }
    return null;
  });
  const [query, setQuery] = useState("");

  const currentDirection = useMemo(() => {
    if (!selectedDirectionId) return null;
    return SPECIALIZATION_DIRECTIONS.find((d) => d.id === selectedDirectionId) || null;
  }, [selectedDirectionId]);

  // Global search filtering across all specializations
  const searchResults = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return null;

    return SPECIALIZATIONS.filter(
      (s) =>
        s.titleRu.toLowerCase().includes(trimmed) ||
        s.titleEn.toLowerCase().includes(trimmed) ||
        s.displayName.toLowerCase().includes(trimmed) ||
        s.name.toLowerCase().includes(trimmed),
    );
  }, [query]);

  // Selected item metadata
  const selectedSpec = useMemo(() => {
    return value ? findSpecialization(value) : null;
  }, [value]);

  const isSpecSelected = (spec: SpecializationItem) => {
    if (!value) return false;
    const v = value.toLowerCase().trim();
    return (
      v === spec.name.toLowerCase() ||
      v === spec.displayName.toLowerCase() ||
      v === spec.titleRu.toLowerCase() ||
      v === spec.titleEn.toLowerCase()
    );
  };

  if (disabled && selectedSpec) {
    return (
      <div
        data-testid="specialization-disabled-card"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          padding: "14px 16px",
          backgroundColor: "var(--color-canvas)",
          borderRadius: "var(--radius-input)",
          border: "1px solid var(--color-frost-border)",
          cursor: "default",
          userSelect: "none",
        }}
      >
        <div style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-slate)" }}>
          {selectedSpec.titleRu}
        </div>
        <div style={{ fontSize: "12px", color: "var(--color-slate)", opacity: 0.8 }}>
          {selectedSpec.titleEn} ({selectedSpec.displayName})
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="specialization-picker"
      style={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      {/* Search Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          backgroundColor: "var(--color-canvas)",
          border: "1px solid var(--color-frost-border)",
          borderRadius: "var(--radius-input)",
          padding: "10px 14px",
        }}
      >
        <Search size={18} color="var(--color-slate)" style={{ flexShrink: 0 }} />
        <input
          data-testid="specialization-search"
          type="text"
          placeholder="Поиск специальности..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          style={{
            border: "none",
            background: "transparent",
            outline: "none",
            fontSize: "14px",
            color: "var(--color-graphite)",
            width: "100%",
          }}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px",
              fontSize: "12px",
              color: "var(--color-slate)",
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Currently Selected Spec Banner if any and not searching */}
      {selectedSpec && !query && (
        <div
          data-testid="specialization-selected-banner"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 14px",
            backgroundColor: "var(--color-gold-wash)",
            borderRadius: "var(--radius-input)",
            border: "1px solid rgba(200, 61, 122, 0.3)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                backgroundColor: "var(--color-muted-gold)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Check size={12} color="var(--color-paper)" />
            </div>
            <div>
              <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--color-graphite)" }}>
                {selectedSpec.titleRu}
              </div>
              <div style={{ fontSize: "11px", color: "var(--color-slate)" }}>
                {selectedSpec.titleEn}
              </div>
            </div>
          </div>
          <span style={{ fontSize: "12px", fontWeight: 600, color: "var(--color-graphite)" }}>
            Выбрано
          </span>
        </div>
      )}

      {/* Main List Container */}
      <div
        data-testid="specializations-list"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          maxHeight: "280px",
          overflowY: "auto",
          paddingRight: "2px",
        }}
      >
        {/* Case A: Search Results View */}
        {searchResults !== null ? (
          searchResults.length === 0 ? (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                color: "var(--color-slate)",
                fontSize: "14px",
              }}
            >
              Ничего не найдено по запросу «{query}»
            </div>
          ) : (
            searchResults.map((spec) => {
              const selected = isSpecSelected(spec);
              const dir = findDirectionForSpecialization(spec.name);
              return (
                <button
                  key={spec.name}
                  type="button"
                  data-testid={`search-result-${spec.name}`}
                  disabled={disabled || disabledOptions.includes(spec.name)}
                  onClick={() => {
                    onChange(spec.name);
                    setSelectedDirectionId(dir ? dir.id : null);
                    setQuery("");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "14px",
                    border: selected
                      ? "1px solid rgba(200, 61, 122, 0.4)"
                      : "1px solid var(--color-frost-border)",
                    backgroundColor: selected
                      ? "var(--color-gold-wash)"
                      : "var(--color-paper)",
                    color: "var(--color-graphite)",
                    textAlign: "left",
                    cursor: disabled || disabledOptions.includes(spec.name) ? "not-allowed" : "pointer",
                    opacity: disabledOptions.includes(spec.name) && !selected ? 0.5 : 1,
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "6px",
                        border: selected
                          ? "1.5px solid var(--color-muted-gold)"
                          : "1.5px solid var(--color-slate)",
                        backgroundColor: selected
                          ? "var(--color-muted-gold)"
                          : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {selected && <Check size={14} color="var(--color-paper)" />}
                    </div>
                    <div>
                      <div style={{ fontSize: "14px", fontWeight: 600 }}>{spec.titleRu}</div>
                      <div style={{ fontSize: "12px", color: "var(--color-slate)" }}>
                        {spec.titleEn} {dir && `• ${dir.titleRu}`}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )
        ) : currentDirection ? (
          /* Case B: Specializations Inside Selected Direction */
          <div>
            {/* Header: Back Button + Direction Name */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 4px 6px 4px",
                marginBottom: "4px",
              }}
            >
              <button
                type="button"
                data-testid="back-to-directions-btn"
                onClick={() => setSelectedDirectionId(null)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "var(--color-slate)",
                  padding: "4px 0",
                }}
              >
                <ArrowLeft size={16} />
                Все направления
              </button>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "var(--color-graphite)" }}>
                {currentDirection.shortTitleRu || currentDirection.titleRu}
              </span>
            </div>

            {/* List of Specializations in Direction */}
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {currentDirection.specializations.map((spec) => {
                const selected = isSpecSelected(spec);
                return (
                  <button
                    key={spec.name}
                    type="button"
                    data-testid={`spec-pill-${spec.name}`}
                    disabled={disabled || disabledOptions.includes(spec.name)}
                    onClick={() => onChange(spec.name)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "14px",
                      border: selected
                        ? "1px solid rgba(200, 61, 122, 0.4)"
                        : "1px solid var(--color-frost-border)",
                      backgroundColor: selected
                        ? "var(--color-gold-wash)"
                        : "var(--color-paper)",
                      color: "var(--color-graphite)",
                      textAlign: "left",
                      cursor: disabled || disabledOptions.includes(spec.name) ? "not-allowed" : "pointer",
                      opacity: disabledOptions.includes(spec.name) && !selected ? 0.5 : 1,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "6px",
                          border: selected
                            ? "1.5px solid var(--color-muted-gold)"
                            : "1.5px solid var(--color-slate)",
                          backgroundColor: selected
                            ? "var(--color-muted-gold)"
                            : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {selected && <Check size={14} color="var(--color-paper)" />}
                      </div>
                      <div>
                        <div style={{ fontSize: "14px", fontWeight: 600 }}>{spec.titleRu}</div>
                        <div style={{ fontSize: "12px", color: "var(--color-slate)" }}>
                          {spec.titleEn}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Case C: List of Directions (Categories) */
          SPECIALIZATION_DIRECTIONS.map((dir) => {
            const hasSelectedSpec =
              value && dir.specializations.some((s) => isSpecSelected(s));

            return (
              <button
                key={dir.id}
                type="button"
                data-testid={`direction-item-${dir.id}`}
                onClick={() => setSelectedDirectionId(dir.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: "14px",
                  border: hasSelectedSpec
                    ? "1px solid rgba(200, 61, 122, 0.4)"
                    : "1px solid var(--color-frost-border)",
                  backgroundColor: hasSelectedSpec
                    ? "var(--color-gold-wash)"
                    : "var(--color-paper)",
                  color: "var(--color-graphite)",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "6px",
                      border: hasSelectedSpec
                        ? "1.5px solid var(--color-muted-gold)"
                        : "1.5px solid var(--color-frost-border)",
                      backgroundColor: hasSelectedSpec
                        ? "var(--color-muted-gold)"
                        : "var(--color-canvas)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {hasSelectedSpec && <Check size={14} color="var(--color-paper)" />}
                  </div>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 600 }}>{dir.titleRu}</div>
                    <div style={{ fontSize: "12px", color: "var(--color-slate)" }}>
                      {dir.titleEn}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "var(--color-slate)",
                  }}
                >
                  <ArrowRight size={16} />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SpecializationPicker;

